import { useState, useRef, useCallback, useEffect } from 'react';
import {
  isSpeechRecognitionSupported,
  getSpeechRecognition,
  requestMicrophoneAccess
} from '../utils/speechRecognition';

/**
 * Hook för röstinmatning med Web Speech API
 *
 * @param {Object} options
 * @param {string} options.language - Språkkod (default: 'sv-SE')
 * @param {boolean} options.continuous - Kontinuerlig inspelning (default: true)
 * @param {boolean} options.interimResults - Visa mellanresultat (default: true)
 * @param {function} options.onResult - Callback när transkribering är klar (text, confidence)
 * @param {function} options.onError - Callback vid fel (error)
 * @param {function} options.onInterim - Callback för mellanresultat (text)
 */
export function useVoiceRecognition({
  language = 'sv-SE',
  continuous = true,
  interimResults = true,
  onResult,
  onError,
  onInterim
} = {}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported] = useState(isSpeechRecognitionSupported());
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState(null);
  const [permissionState, setPermissionState] = useState('unknown');

  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);

  // Spåra vad som redan skickats för att undvika duplicering (mobilproblem)
  const lastSentTextRef = useRef('');
  const accumulatedFinalRef = useRef('');

  // Spara callbacks i refs för att undvika att återskapa recognition-objektet
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const onInterimRef = useRef(onInterim);

  // Uppdatera refs när callbacks ändras
  useEffect(() => {
    onResultRef.current = onResult;
    onErrorRef.current = onError;
    onInterimRef.current = onInterim;
  }, [onResult, onError, onInterim]);

  // Initiera recognition objekt
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognition = getSpeechRecognition();
    const recognition = new SpeechRecognition();

    recognition.lang = language;
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      isListeningRef.current = true;
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      isListeningRef.current = false;
      setIsListening(false);

      // Om vi fortfarande ska lyssna (continuous), starta om
      // (Chrome stoppar ibland automatiskt)
    };

    recognition.onerror = (event) => {
      isListeningRef.current = false;
      setIsListening(false);

      // Hantera "aborted" tyst - det är förväntat beteende vid stopp-kommando
      if (event.error === 'aborted') {
        return;
      }

      console.error('Speech recognition error:', event.error);

      let errorMessage = 'Ett fel uppstod vid röstinmatning';
      switch (event.error) {
        case 'not-allowed':
          errorMessage = 'Mikrofonåtkomst nekad. Tillåt mikrofon i webbläsaren.';
          setPermissionState('denied');
          break;
        case 'no-speech':
          errorMessage = 'Inget tal upptäcktes. Försök igen.';
          break;
        case 'audio-capture':
          errorMessage = 'Mikrofon hittades inte eller är upptagen.';
          break;
        case 'network':
          errorMessage = 'Nätverksfel. Kontrollera din anslutning.';
          break;
        default:
          errorMessage = `Fel: ${event.error}`;
      }

      setError(errorMessage);
      onErrorRef.current?.(errorMessage);
    };

    recognition.onresult = (event) => {
      let interim = '';
      let fullFinal = '';
      let conf = 0;

      // Bygg upp HELA finala transkriptet från alla resultat
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;

        if (result.isFinal) {
          fullFinal += transcript;
          conf = result[0].confidence;
        } else {
          interim += transcript;
        }
      }

      if (interim) {
        setInterimTranscript(interim);
        onInterimRef.current?.(interim);
      }

      if (fullFinal) {
        // Spara hela transkriptet
        accumulatedFinalRef.current = fullFinal;

        setFinalTranscript(fullFinal);
        setConfidence(conf);
        setInterimTranscript('');

        // Skicka HELA transkriptet - komponenten ska ERSÄTTA, inte lägga till
        // Tredje parametern (true) indikerar att det är fullständigt transkript
        onResultRef.current?.(fullFinal.trim(), conf, true);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (err) {
          // Ignorera fel vid cleanup
        }
      }
    };
  }, [isSupported, language, continuous, interimResults]);

  // Starta inspelning
  const startListening = useCallback(async () => {
    if (!isSupported) {
      setError('Röstinmatning stöds inte i denna webbläsare');
      return false;
    }

    if (isListeningRef.current) {
      return true; // Redan igång
    }

    // Kontrollera/begär mikrofontillstånd
    const hasPermission = await requestMicrophoneAccess();
    if (!hasPermission) {
      setPermissionState('denied');
      setError('Mikrofonåtkomst nekad. Tillåt mikrofon i webbläsaren.');
      return false;
    }
    setPermissionState('granted');

    // Rensa tidigare transkript och spårning
    setFinalTranscript('');
    setInterimTranscript('');
    setConfidence(0);
    setError(null);
    lastSentTextRef.current = '';
    accumulatedFinalRef.current = '';

    try {
      recognitionRef.current?.start();
      return true;
    } catch (err) {
      console.error('Fel vid start av röstinmatning:', err);
      setError('Kunde inte starta röstinmatning');
      return false;
    }
  }, [isSupported]);

  // Stoppa inspelning
  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.stop();
    } catch (err) {
      // Ignorera fel vid stopp
    }
    setIsListening(false);
    isListeningRef.current = false;
  }, []);

  // Avbryt inspelning (utan att trigga onresult)
  const abortListening = useCallback(() => {
    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.abort();
    } catch (err) {
      // Ignorera fel vid abort
    }
    setIsListening(false);
    isListeningRef.current = false;
    setInterimTranscript('');
  }, []);

  // Rensa transkript
  const clearTranscript = useCallback(() => {
    setFinalTranscript('');
    setInterimTranscript('');
    setConfidence(0);
    lastSentTextRef.current = '';
    accumulatedFinalRef.current = '';
  }, []);

  return {
    isListening,
    isSupported,
    interimTranscript,
    finalTranscript,
    confidence,
    error,
    permissionState,
    startListening,
    stopListening,
    abortListening,
    clearTranscript
  };
}

export default useVoiceRecognition;
